<template>
  <div class="bg-gray-50">
  <div class="space-y-6">
      <!-- 统计卡片 -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <MegaphoneIcon class="h-5 w-5 text-blue-600" />
              </div>
            </div>
            <div class="ml-4">
              <p class="text-2xl font-bold text-gray-900">{{ stats.totalCampaigns }}</p>
              <p class="text-sm text-gray-500">总活动数</p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircleIcon class="h-5 w-5 text-green-600" />
              </div>
            </div>
            <div class="ml-4">
              <p class="text-2xl font-bold text-gray-900">{{ stats.activeCampaigns }}</p>
              <p class="text-sm text-gray-500">进行中</p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <EnvelopeIcon class="h-5 w-5 text-purple-600" />
              </div>
            </div>
            <div class="ml-4">
              <p class="text-2xl font-bold text-gray-900">{{ stats.totalEmailsSent.toLocaleString() }}</p>
              <p class="text-sm text-gray-500">邮件已发送</p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <EyeIcon class="h-5 w-5 text-orange-600" />
              </div>
            </div>
            <div class="ml-4">
              <p class="text-2xl font-bold text-gray-900">{{ stats.avgOpenRate }}%</p>
              <p class="text-sm text-gray-500">平均打开率</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 搜索和筛选 -->
      <div class="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div class="p-4">
          <div class="flex flex-col sm:flex-row gap-4">
            <!-- 搜索框 -->
            <div class="flex-1">
              <div class="relative">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="搜索活动名称、主题..."
                  class="w-full h-10 pl-10 pr-4 text-sm bg-gray-50/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all duration-200 placeholder-gray-400"
                  @keyup.enter="handleSearch"
                >
                <MagnifyingGlassIcon class="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>

            <!-- 筛选器和按钮 -->
            <div class="flex gap-2">
              <select
                v-model="selectedStatus"
                @change="handleSearch"
                class="w-32 h-10 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white transition-all duration-200"
              >
                <option value="">全部状态</option>
                <option value="draft">草稿</option>
                <option value="scheduled">已计划</option>
                <option value="sending">发送中</option>
                <option value="completed">已完成</option>
                <option value="paused">已暂停</option>
              </select>

              <button
                @click="handleSearch"
                :disabled="loading"
                class="inline-flex items-center justify-center h-10 px-4 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MagnifyingGlassIcon class="h-4 w-4 mr-2" />
                搜索<span class="hidden sm:inline">活动</span>
              </button>

              <button 
                @click="openCreateModal"
                class="inline-flex items-center justify-center h-10 px-4 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2 shadow-sm hover:shadow-md transform hover:scale-105 transition-all duration-200"
              >
                <PlusIcon class="h-4 w-4 mr-2" />
                创建<span class="hidden sm:inline">活动</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 活动列表 -->
      <div>
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-gray-900">营销活动</h3>
          <span class="text-sm text-gray-500">共 {{ totalCampaigns }} 个活动</span>
        </div>

        <!-- 加载状态 -->
        <div v-if="loading" class="flex justify-center items-center py-12">
          <div class="inline-flex items-center">
            <svg class="animate-spin h-5 w-5 mr-3 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span class="text-gray-600">加载中...</span>
          </div>
      </div>

        <!-- 空状态 -->
        <div v-else-if="filteredCampaigns.length === 0" class="text-center py-12">
          <MegaphoneIcon class="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p class="text-lg font-medium text-gray-900 mb-2">
            {{ searchQuery || selectedStatus ? '未找到匹配的活动' : '暂无营销活动' }}
          </p>
          <p class="text-sm text-gray-500 mb-4">
            {{ searchQuery || selectedStatus ? '请尝试调整搜索条件' : '创建您的第一个邮件营销活动' }}
          </p>
          <button 
            v-if="!searchQuery && !selectedStatus"
            @click="openCreateModal"
            class="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors duration-200"
          >
        <PlusIcon class="h-4 w-4 mr-2" />
        创建活动
      </button>
    </div>

        <!-- 卡片网格 -->
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          <div 
            v-for="campaign in filteredCampaigns" 
            :key="campaign.id"
            class="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
          >
            <!-- 卡片头部 -->
            <div class="p-6 pb-4">
              <div class="flex items-start justify-between mb-3">
                <div class="flex-1 min-w-0">
                  <h4 class="text-lg font-medium text-gray-900 truncate mb-1">{{ campaign.name }}</h4>
                  <span 
                    :class="getStatusBadgeClass(campaign.status)"
                    class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                  >
                    {{ getStatusText(campaign.status) }}
                  </span>
                </div>
                
                <!-- 下拉菜单 -->
                <div class="relative ml-2">
                  <button
                    @click="toggleDropdown(campaign.id)"
                    class="dropdown-button inline-flex items-center p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors duration-200"
                  >
                    <EllipsisVerticalIcon class="h-5 w-5" />
                  </button>
                  
                  <div 
                    v-if="activeDropdown === campaign.id"
                    class="dropdown-menu absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10"
                  >
                    <div class="py-1">
                      <button
                        @click="editCampaign(campaign)"
                        :disabled="campaign.status !== 'draft' || editing === campaign.id"
                        class="w-full text-left px-4 py-2 text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                        :class="campaign.status === 'draft' && editing !== campaign.id ? 'text-gray-700 hover:bg-gray-50' : 'text-gray-400'"
                      >
                        <svg v-if="editing === campaign.id" class="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <PencilIcon v-else class="h-4 w-4 mr-2" />
                        {{ editing === campaign.id ? '加载中...' : '编辑活动' }}
                      </button>
                      <button
                        @click="duplicateCampaign(campaign)"
                        class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                      >
                        <DocumentDuplicateIcon class="h-4 w-4 mr-2" />
                        复制活动
                      </button>
                      <button
                        @click="viewLogs(campaign)"
                        class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                      >
                        <ChartBarIcon class="h-4 w-4 mr-2" />
                        查看统计
                      </button>
                      <div class="border-t border-gray-100"></div>
                      <button
                        @click="deleteCampaign(campaign)"
                        :disabled="deleting === campaign.id"
                        class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <TrashIcon v-if="deleting !== campaign.id" class="h-4 w-4 mr-2" />
                        <svg v-else class="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {{ deleting === campaign.id ? '删除中...' : '删除活动' }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              <p class="text-sm text-gray-500 truncate mb-4">{{ campaign.description || '暂无描述' }}</p>
              
              <!-- 统计信息 -->
              <div class="grid grid-cols-2 gap-4 mb-4">
                <div class="text-center p-3 bg-gray-50 rounded-lg">
                  <div class="text-lg font-semibold text-gray-900">{{ (campaign.emails_sent || 0).toLocaleString() }}</div>
                  <div class="text-xs text-gray-500">已发送</div>
                </div>
                <div class="text-center p-3 bg-gray-50 rounded-lg">
                  <div class="text-lg font-semibold text-gray-900">{{ (campaign.open_rate || 0).toFixed(1) }}%</div>
                  <div class="text-xs text-gray-500">打开率</div>
                </div>
              </div>

              <!-- 进度条（所有活动显示） -->
              <div class="mb-4">
                <div class="flex items-center justify-between text-sm text-gray-600 mb-2">
                  <span>{{ campaign.progress_label || getProgressLabel(campaign.status) }}</span>
                  <span>{{ campaign.progress_percentage !== undefined ? campaign.progress_percentage : getProgressPercentage(campaign) }}%</span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    :class="getProgressBarClass(campaign.status)"
                    class="h-2 rounded-full transition-all duration-300"
                    :style="{ width: (campaign.progress_percentage !== undefined ? campaign.progress_percentage : getProgressPercentage(campaign)) + '%' }"
                  ></div>
                </div>
              </div>

              <!-- 详细信息 -->
              <div class="space-y-2 text-sm text-gray-500 mb-4">
                <div class="flex items-center">
                  <CalendarIcon class="h-4 w-4 mr-2 flex-shrink-0" />
                  <span class="truncate">{{ formatDate(campaign.created_at) }}</span>
                </div>
                <div class="flex items-center">
                  <ClockIcon class="h-4 w-4 mr-2 flex-shrink-0" />
                  <span class="truncate">
                    <template v-if="campaign.send_type === 'scheduled' && campaign.scheduled_time">
                      {{ formatDate(campaign.scheduled_time) }}
                    </template>
                    <template v-else>
                      {{ campaign.send_type_display || '立即发送' }}
                    </template>
                  </span>
                </div>
                <div class="flex items-center">
                  <UsersIcon class="h-4 w-4 mr-2 flex-shrink-0" />
                  <span>目标客户：{{ (campaign.total_recipients || 0).toLocaleString() }}</span>
                </div>
                <div class="flex items-center">
                  <UserIcon class="h-4 w-4 mr-2 flex-shrink-0" />
                  <span class="truncate">
                    创建者：{{ campaign.created_by_username || '未知' }}
                    <span 
                      v-if="campaign.creator_account_type" 
                      class="ml-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                      :class="{
                        'bg-blue-100 text-blue-800': campaign.creator_account_type === 'master',
                        'bg-orange-100 text-orange-800': campaign.creator_account_type === 'sub'
                      }"
                    >
                      {{ campaign.creator_account_type_display }}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <!-- 卡片底部操作区 -->
            <div class="px-6 py-4 bg-gray-50 border-t border-gray-100">
              <div class="flex space-x-2">
                <button
                  v-if="isCompletedCampaign(campaign)"
                  @click="viewLogs(campaign)"
                  class="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
                >
                  <ChartBarIcon class="h-4 w-4 mr-1" />
                  查看统计
                </button>
                
                <button
                  v-if="campaign.status === 'draft'"
                  @click="startCampaign(campaign)"
                  :disabled="starting === campaign.id"
                  class="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlayIcon v-if="starting !== campaign.id" class="h-4 w-4 mr-1" />
                  <svg v-else class="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span class="ml-1">{{ starting === campaign.id ? '启动中' : '启动' }}</span>
                </button>

                <button
                  v-else-if="campaign.status === 'sending' || campaign.status === 'scheduled'"
                  @click="pauseCampaign(campaign)"
                  :disabled="pausing === campaign.id"
                  class="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 focus:ring-2 focus:ring-yellow-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PauseIcon v-if="pausing !== campaign.id" class="h-4 w-4 mr-1" />
                  <svg v-else class="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span class="ml-1">{{ pausing === campaign.id ? '暂停中' : '暂停' }}</span>
                </button>

                <button
                  v-else-if="campaign.status === 'paused'"
                  @click="resumeCampaign(campaign)"
                  :disabled="resuming === campaign.id"
                  class="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlayIcon v-if="resuming !== campaign.id" class="h-4 w-4 mr-1" />
                  <svg v-else class="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span class="ml-1">{{ resuming === campaign.id ? '恢复中' : '恢复' }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 加载更多指示器 -->
        <div v-if="hasMore && !loading" class="mt-6 text-center">
          <div v-if="isLoadingMore" class="flex justify-center items-center py-6">
            <svg class="animate-spin h-6 w-6 text-gray-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <div v-else class="py-4">
            <p class="text-sm text-gray-500">向下滚动加载更多活动</p>
          </div>
        </div>
        
        <!-- 没有更多数据 -->
        <div v-if="!hasMore && !loading && filteredCampaigns.length > 0" class="mt-6 text-center py-4">
          <p class="text-sm text-gray-500">已显示全部 {{ totalCampaigns }} 个活动</p>
      </div>
      </div>
    </div>

    <!-- 营销活动表单模态框 -->
    <CampaignFormModal
      :visible="showFormModal"
      :campaign="selectedCampaign"
      @close="closeFormModal"
      @submit="handleFormSubmit"
    />

    <!-- 邮件发送日志模态框 -->
    <EmailLogsModal
      :visible="showLogsModal"
      :campaign="selectedCampaignForLogs"
      @close="closeLogsModal"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useToast } from 'vue-toastification'
import { 
  PlusIcon, 
  MegaphoneIcon, 
  MagnifyingGlassIcon, 
  EyeIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  CalendarIcon,
  ClockIcon,
  UsersIcon,
  UserIcon,
  PlayIcon,
  PauseIcon,
  DocumentDuplicateIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  ChartBarIcon,
  ArrowDownTrayIcon,
  TrashIcon
} from '@heroicons/vue/24/outline'
import CampaignFormModal from '@/components/campaigns/CampaignFormModal.vue'
import EmailLogsModal from '@/components/campaigns/EmailLogsModal.vue'
import campaignService from '@/services/campaignService'

// Toast 实例
const toast = useToast()

// 响应式数据
const loading = ref(false)
const searchQuery = ref('')
const selectedStatus = ref('')
const activeDropdown = ref(null)
const showFormModal = ref(false)
const selectedCampaign = ref(null)
const showLogsModal = ref(false)
const selectedCampaignForLogs = ref(null)
const starting = ref(null)
const pausing = ref(null)
const resuming = ref(null)
const deleting = ref(null)
const editing = ref(null)

// 滚动加载相关
const pageSize = ref(12)
const loadedCount = ref(12)
const isLoadingMore = ref(false)
const hasMoreData = ref(true)

// 响应式数据
const stats = ref({
  totalCampaigns: 0,
  activeCampaigns: 0,
  totalEmailsSent: 0,
  avgOpenRate: 0
})

const campaigns = ref([])
const totalCampaigns = ref(0)

// 计算属性
const allFilteredCampaigns = computed(() => {
  return campaigns.value
})

const filteredCampaigns = computed(() => {
  return campaigns.value.slice(0, loadedCount.value)
})

const hasMore = computed(() => {
  return loadedCount.value < totalCampaigns.value
})

// 初始化数据加载
const loadInitialData = async () => {
  try {
    loading.value = true
    
    // 并行加载统计信息和活动列表
    const [statsResult, campaignsResult] = await Promise.all([
      campaignService.getStats(),
      campaignService.getCampaigns({
        page: 1,
        pageSize: pageSize.value,
        status: selectedStatus.value || undefined,
        search: searchQuery.value || undefined
      })
    ])
    
    if (statsResult.success) {
      stats.value = {
        totalCampaigns: statsResult.data.total_campaigns || 0,
        activeCampaigns: statsResult.data.active_campaigns || 0,
        totalEmailsSent: statsResult.data.total_emails_sent || 0,
        avgOpenRate: statsResult.data.avg_open_rate || 0
      }
    }
    
    if (campaignsResult.success) {
      campaigns.value = campaignsResult.data.results || []
      totalCampaigns.value = campaignsResult.data.count || 0
      loadedCount.value = campaigns.value.length
    } else {
      showToast('获取活动列表失败', 'error')
    }
    
  } catch (error) {
    console.error('加载数据失败:', error)
    showToast('加载数据失败', 'error')
  } finally {
    loading.value = false
  }
}

// 方法
const handleSearch = async () => {
  try {
    loading.value = true
    loadedCount.value = pageSize.value
    isLoadingMore.value = false
    
    const result = await campaignService.getCampaigns({
      page: 1,
      pageSize: pageSize.value,
      status: selectedStatus.value || undefined,
      search: searchQuery.value || undefined
    })
    
    if (result.success) {
      campaigns.value = result.data.results || []
      totalCampaigns.value = result.data.count || 0
      loadedCount.value = campaigns.value.length
    } else {
      showToast('搜索失败', 'error')
    }
  } catch (error) {
    console.error('搜索失败:', error)
    showToast('搜索失败', 'error')
  } finally {
    loading.value = false
  }
}

const getStatusBadgeClass = (status) => {
  const classes = {
    'draft': 'bg-gray-100 text-gray-800',
    'scheduled': 'bg-blue-100 text-blue-800',
    'sending': 'bg-yellow-100 text-yellow-800',
    'completed': 'bg-green-100 text-green-800',
    'paused': 'bg-orange-100 text-orange-800',
    'cancelled': 'bg-red-100 text-red-800'
  }
  return classes[status] || 'bg-gray-100 text-gray-800'
}

const getStatusText = (status) => {
  const texts = {
    'draft': '草稿',
    'scheduled': '已计划',
    'sending': '发送中',
    'completed': '已完成',
    'paused': '已暂停',
    'cancelled': '已取消'
  }
  return texts[status] || '未知'
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getProgressLabel = (status) => {
  const labels = {
    'draft': '发送进度-暂未开始',
    'scheduled': '发送进度-暂未开始',
    'sending': '发送进度',
    'completed': '发送进度',
    'paused': '发送进度',
    'cancelled': '发送进度'
  }
  return labels[status] || '发送进度'
}

const getProgressPercentage = (campaign) => {
  switch (campaign.status) {
    case 'draft':
    case 'scheduled':
      return 0
    case 'completed':
      return 100
    case 'sending':
    case 'paused':
    case 'cancelled':
      if (campaign.total_recipients > 0) {
        return Math.round((campaign.emails_sent / campaign.total_recipients) * 100)
      }
      return 0
    default:
      return 0
  }
}

const getProgressBarClass = (status) => {
  const classes = {
    'draft': 'bg-gray-300',
    'scheduled': 'bg-gray-300',
    'sending': 'bg-blue-600',
    'completed': 'bg-green-500',
    'paused': 'bg-yellow-500',
    'cancelled': 'bg-red-500'
  }
  return classes[status] || 'bg-gray-300'
}

const loadMore = async () => {
  if (isLoadingMore.value || !hasMore.value) return
  
  isLoadingMore.value = true
  
  try {
    const currentPage = Math.floor(campaigns.value.length / pageSize.value) + 1
    const result = await campaignService.getCampaigns({
      page: currentPage,
      pageSize: pageSize.value,
      status: selectedStatus.value || undefined,
      search: searchQuery.value || undefined
    })
    
    if (result.success) {
      const newCampaigns = result.data.results || []
      campaigns.value = [...campaigns.value, ...newCampaigns]
      loadedCount.value = campaigns.value.length
      totalCampaigns.value = result.data.count || 0
    }
  } catch (error) {
    console.error('加载更多失败:', error)
    showToast('加载更多失败', 'error')
  } finally {
    isLoadingMore.value = false
  }
}

const handleScroll = () => {
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  const windowHeight = window.innerHeight
  const documentHeight = document.documentElement.scrollHeight
  
  // 当滚动到距离底部200px时开始加载
  if (scrollTop + windowHeight >= documentHeight - 200) {
    loadMore()
  }
}

const toggleDropdown = (campaignId) => {
  activeDropdown.value = activeDropdown.value === campaignId ? null : campaignId
}

// 判断活动是否可以查看统计
const isCompletedCampaign = (campaign) => {
  // 只有已启动且有发送数据的活动才能查看统计
  // 包括：sending(发送中), paused(已暂停), completed(已完成), sent(已发送)
  // 并且已发送数量大于0或总收件人大于0
  const dataStates = ['sending', 'paused', 'completed', 'sent']
  return dataStates.includes(campaign.status) && 
         (campaign.emails_sent > 0 || campaign.total_recipients > 0)
}

const openCreateModal = () => {
  selectedCampaign.value = null
  showFormModal.value = true
}

const closeFormModal = () => {
  showFormModal.value = false
  selectedCampaign.value = null
  activeDropdown.value = null
}

const closeLogsModal = () => {
  showLogsModal.value = false
  selectedCampaignForLogs.value = null
}

const handleFormSubmit = async (result) => {
  try {
    // formData参数实际上是CampaignFormModal传过来的API调用结果
    // CampaignFormModal已经处理了API调用和toast提示
    // 这里只需要检查结果并刷新列表
    if (result && result.success) {
      // 重新加载数据以获取最新列表
      await loadInitialData()
    } else {
      // 如果有错误，CampaignFormModal已经显示了toast
      console.error('操作失败:', result?.error)
    }
    
    closeFormModal()
  } catch (error) {
    console.error('刷新列表失败:', error)
    showToast('刷新列表失败，请重试', 'error')
  }
}

const startCampaign = async (campaign) => {
  starting.value = campaign.id
  
  try {
    const result = await campaignService.startCampaign(campaign.id)
    if (result.success) {
      campaign.status = result.data.status || 'sending'
      stats.value.activeCampaigns++
      showToast(result.data?.message || '活动启动成功', 'success')
    } else {
      showToast(result.error || '启动失败', 'error')
    }
  } catch (error) {
    console.error('启动活动失败:', error)
    showToast('启动失败，请重试', 'error')
  } finally {
    starting.value = null
  }
}

const pauseCampaign = async (campaign) => {
  pausing.value = campaign.id
  
  try {
    const result = await campaignService.pauseCampaign(campaign.id)
    if (result.success) {
      campaign.status = result.data.status || 'paused'
      if (stats.value.activeCampaigns > 0) {
        stats.value.activeCampaigns--
      }
      showToast(result.data?.message || '活动已暂停', 'success')
    } else {
      showToast(result.error || '暂停失败', 'error')
    }
  } catch (error) {
    console.error('暂停活动失败:', error)
    showToast('暂停失败，请重试', 'error')
  } finally {
    pausing.value = null
  }
}

const resumeCampaign = async (campaign) => {
  resuming.value = campaign.id
  
  try {
    const result = await campaignService.resumeCampaign(campaign.id)
    if (result.success) {
      campaign.status = result.data.status || 'sending'
      stats.value.activeCampaigns++
      showToast(result.data?.message || '活动已恢复', 'success')
    } else {
      showToast(result.error || '恢复失败', 'error')
    }
  } catch (error) {
    console.error('恢复活动失败:', error)
    showToast('恢复失败，请重试', 'error')
  } finally {
    resuming.value = null
  }
}

const duplicateCampaign = async (campaign) => {
  try {
    const result = await campaignService.duplicateCampaign(campaign.id)
    if (result.success) {
      showToast(result.data?.message || '活动复制成功', 'success')
      // 重新加载数据以显示新的副本
      await loadInitialData()
    } else {
      showToast(result.error || '复制失败', 'error')
    }
  } catch (error) {
    console.error('复制活动失败:', error)
    showToast('复制失败，请重试', 'error')
  }
  activeDropdown.value = null
}

const canEditCampaign = (campaign) => {
  // 只允许编辑草稿状态的活动
  return campaign.status === 'draft'
}

const editCampaign = async (campaign) => {
  // 检查是否可以编辑
  if (!canEditCampaign(campaign)) {
    showToast('只有未启动的活动才能编辑', 'warning')
    activeDropdown.value = null
    return
  }
  
  editing.value = campaign.id
  
  try {
    // 获取完整的活动详情数据
    const result = await campaignService.getCampaign(campaign.id)
    if (result.success) {
      selectedCampaign.value = result.data
      showFormModal.value = true
      activeDropdown.value = null
    } else {
      showToast('获取活动详情失败', 'error')
      activeDropdown.value = null
    }
  } catch (error) {
    console.error('获取活动详情失败:', error)
    showToast('获取活动详情失败，请重试', 'error')
    activeDropdown.value = null
  } finally {
    editing.value = null
  }
}

const viewLogs = (campaign) => {
  selectedCampaignForLogs.value = campaign
  showLogsModal.value = true
  activeDropdown.value = null
  activeDropdown.value = null
}

const exportData = async (campaign) => {
  try {
    const result = await campaignService.exportCampaignData(campaign.id, 'csv')
    if (result.success) {
      showToast('数据导出成功', 'success')
    } else {
      showToast(result.error, 'error')
    }
  } catch (error) {
    showToast('导出失败，请重试', 'error')
  }
  activeDropdown.value = null
}

const deleteCampaign = async (campaign) => {
  if (!confirm(`确定要删除活动"${campaign.name}"吗？此操作不可撤销。`)) {
    activeDropdown.value = null
    return
  }

  deleting.value = campaign.id

  try {
    const result = await campaignService.deleteCampaign(campaign.id)
    if (result.success) {
      const index = campaigns.value.findIndex(c => c.id === campaign.id)
      if (index > -1) {
        campaigns.value.splice(index, 1)
        totalCampaigns.value = Math.max(0, totalCampaigns.value - 1)
        if (stats.value.totalCampaigns > 0) {
          stats.value.totalCampaigns--
        }
        if (campaign.status === 'sending' && stats.value.activeCampaigns > 0) {
          stats.value.activeCampaigns--
        }
      }
      showToast('活动删除成功', 'success')
    } else {
      showToast(result.error || '删除失败', 'error')
    }
  } catch (error) {
    console.error('删除活动失败:', error)
    showToast('删除失败，请重试', 'error')
  } finally {
    deleting.value = null
  }
  
  activeDropdown.value = null
}

// 显示提示消息
const showToast = (message, type = 'info') => {
  switch (type) {
    case 'success':
      toast.success(message)
      break
    case 'error':
      toast.error(message)
      break
    case 'warning':
      toast.warning(message)
      break
    default:
      toast.info(message)
  }
}

// 点击外部关闭下拉菜单
const handleClickOutside = (event) => {
  // 检查点击的元素是否在任何下拉菜单内
  const clickedElement = event.target
  const dropdown = clickedElement.closest('.dropdown-menu')
  const dropdownButton = clickedElement.closest('.dropdown-button')
  
  // 如果点击的不是下拉菜单内容，也不是触发按钮，则关闭下拉菜单
  if (!dropdown && !dropdownButton) {
    activeDropdown.value = null
  }
}

onMounted(async () => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('scroll', handleScroll)
  // 加载初始数据
  await loadInitialData()
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('scroll', handleScroll)
})
</script>
